'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import {
  NestedSheet,
  NestedSheetContent,
  NestedSheetHeader,
  NestedSheetTitle,
  NestedSheetBody,
} from '@/shared/ui';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/ui/shadcn/alert-dialog';
import { useGoalsEditor } from '@/features/project/hooks/use-goals-editor';
import { GoalsCommitButton } from '@/features/project/ui/GoalsCommitButton';
import { BriefTextForm } from '@/features/project/ui/BriefTextForm';
import { BriefMaterials } from '@/features/angles-overview';

interface ProjectGoalsSheetProps {
  projectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * NestedSheet with the WHOLE brief: the source materials grid (views,
 * sketches, documents, references) followed by the text form (goals +
 * construction region + facade area). Both halves are the very components the
 * standalone brief page renders, so the panel and the page cannot drift apart
 * in what they show or what they can do. Shared by the render-settings goals
 * row and the estimate editor's facade-area link. Mirrors Apple's
 * `ProjectGoalsView` sheet and Android's `ProjectGoalsPanelContent`.
 *
 * Since the goals-dialogue pivot, edits are committed explicitly via the teal
 * checkmark (top-right) instead of autosaving per keystroke — the commit fires
 * the server-side LLM evaluation. Closing with unsaved edits asks the user
 * whether to save or discard. Buffer/dirty/commit logic lives in
 * `useGoalsEditor`.
 */
export function ProjectGoalsSheet({
  projectId,
  open,
  onOpenChange,
}: ProjectGoalsSheetProps) {
  const t = useTranslations('projectGoals');
  const tProject = useTranslations('project');

  // The buffer is owned here (the commit affordance lives in the sheet header)
  // and handed to the shared `BriefTextForm`.
  const editor = useGoalsEditor(projectId);
  const { project, isDirty, commit, discard } = editor;
  const isArchived = !!project?.attributes.archived_at;

  const [confirmOpen, setConfirmOpen] = useState(false);
  const requestClose = () => {
    if (isDirty) {
      setConfirmOpen(true);
      return;
    }
    onOpenChange(false);
  };

  return (
    <>
      <NestedSheet
        open={open}
        onOpenChange={(next) => {
          if (!next) {
            requestClose();
            return;
          }
          onOpenChange(true);
        }}
      >
        <NestedSheetContent>
          <NestedSheetHeader
            trailingAction={
              <GoalsCommitButton
                projectId={projectId}
                isDirty={isDirty}
                onCommit={commit}
              />
            }
          >
            <NestedSheetTitle>{t('label')}</NestedSheetTitle>
          </NestedSheetHeader>

          <NestedSheetBody className="px-4">
            <div className="space-y-6 py-2">
              {/* Source materials — the same grid the brief page shows, with the
                  same uploading, sketching and detail affordances. Without it
                  the panel read as a poorer, text-only brief and hid the very
                  photos the render runs on. Files lead, the text form follows
                  (the narrow-column order of the brief page). */}
              <BriefMaterials projectId={projectId} isArchived={isArchived} />

              <BriefTextForm
                editor={editor}
                idPrefix="render"
                isArchived={isArchived}
              />
            </div>
          </NestedSheetBody>
        </NestedSheetContent>
      </NestedSheet>

      {/* Unsaved-changes guard on close. */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {tProject('goalsUnsavedTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {tProject('goalsUnsavedMessage')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => {
                discard();
                setConfirmOpen(false);
                onOpenChange(false);
              }}
            >
              {tProject('goalsUnsavedDiscard')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                commit();
                setConfirmOpen(false);
                onOpenChange(false);
              }}
            >
              {tProject('goalsUnsavedSave')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
